/**
 * useAuth Hook - Authentication Management
 *
 * TanStack Query mutation hooks for authentication:
 * - Login with email + API key
 * - Logout and clear session
 * - Refresh token before expiration
 * - Auto-refresh logic
 * - 401 error handling
 *
 * @see specs/002-frontend-mvp/spec.md - User Story 1
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import { sessionStorage } from '../../../lib/axios';
import { useSession } from './useSession';
import * as authClient from '../api/authClient';
import type { User } from '../../../types/user';
import type { Session } from '../../../types/session';

/**
 * Authentication Hook
 *
 * Provides authentication state and mutations for login, logout, and token refresh.
 */
export function useAuth() {
  const queryClient = useQueryClient();
  const {
    session,
    token,
    refreshToken: refreshTokenValue,
    isExpired,
    needsRefresh,
    setToken: setSessionToken,
    clearToken: clearSessionToken,
  } = useSession();

  /**
   * Extract user from session
   * Use ?? null to ensure undefined from cleared cache becomes null
   */
  const user = session ? (queryClient.getQueryData<User>(['user', session.userId]) ?? null) : null;

  /**
   * Login Mutation
   */
  const loginMutation = useMutation({
    mutationFn: authClient.login,
    onSuccess: (data) => {
      // Create session object
      const newSession: Session = {
        token: data.token,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        userId: data.user.id,
        issuedAt: new Date().toISOString(),
      };

      // Store session in memory
      setSessionToken(newSession);

      // Update axios session storage
      sessionStorage.setToken(data.token);

      // Cache user data
      queryClient.setQueryData(['user', data.user.id], data.user);
    },
    onError: () => {
      // Clear session on login failure
      clearSessionToken();
      sessionStorage.clearTokens();
    },
  });

  /**
   * Logout Mutation
   */
  const logoutMutation = useMutation({
    mutationFn: authClient.logout,
    onSuccess: () => {
      // Clear session
      clearSessionToken();
      sessionStorage.clearTokens();

      // Clear all cached data
      queryClient.clear();
    },
    onError: () => {
      // Clear session even if logout API call fails
      clearSessionToken();
      sessionStorage.clearTokens();
      queryClient.clear();
    },
  });

  /**
   * Refresh Token Mutation
   */
  const refreshMutation = useMutation({
    mutationFn: (refreshToken: string) => authClient.refreshToken({ refreshToken }),
    onSuccess: (data) => {
      if (!session) return;

      // Update session with new token
      const newSession: Session = {
        ...session,
        token: data.token,
        expiresAt: data.expiresAt,
        issuedAt: new Date().toISOString(),
      };

      setSessionToken(newSession);
      sessionStorage.setToken(data.token);
    },
    onError: () => {
      // Refresh failed - clear session and redirect to login
      clearSessionToken();
      sessionStorage.clearTokens();
      queryClient.clear();

      // Redirect to login page (not in test mode)
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login' &&
        import.meta.env.MODE !== 'test'
      ) {
        window.location.href = '/login';
      }
    },
  });

  /**
   * Login Function
   */
  const login = useCallback(
    (credentials: { email: string; apiKey: string }) => {
      loginMutation.mutate(credentials);
    },
    [loginMutation]
  );

  /**
   * Logout Function
   */
  const logout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);

  /**
   * Refresh Session Function
   */
  const refreshSession = useCallback(() => {
    if (refreshTokenValue) {
      refreshMutation.mutate(refreshTokenValue);
    }
  }, [refreshTokenValue, refreshMutation]);

  /**
   * Auto-refresh when token is close to expiration (< 5 minutes)
   */
  useEffect(() => {
    if (needsRefresh() && refreshTokenValue && !refreshMutation.isPending) {
      refreshSession();
    }
  }, [needsRefresh, refreshTokenValue, refreshMutation.isPending, refreshSession, session]);

  /**
   * Handle session expiration
   */
  useEffect(() => {
    if (isExpired && session) {
      // Session expired - log out
      clearSessionToken();
      sessionStorage.clearTokens();
      queryClient.clear();

      // Redirect to login page (not in test mode)
      if (
        typeof window !== 'undefined' &&
        window.location.pathname !== '/login' &&
        import.meta.env.MODE !== 'test'
      ) {
        window.location.href = '/login';
      }
    }
  }, [isExpired, session, clearSessionToken, queryClient]);

  /**
   * Extract error message in API format if available
   */
  const error = loginMutation.error || logoutMutation.error || refreshMutation.error;
  const formattedError = error
    ? {
        error: (error as any)?.response?.data?.error || 'UNKNOWN_ERROR',
        message: (error as any)?.response?.data?.message || error.message,
      }
    : null;

  return {
    // State
    session,
    user,
    token,
    isAuthenticated: !!session && !isExpired,
    isExpired,

    // Mutations
    login,
    logout,
    refreshSession,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isRefreshing: refreshMutation.isPending,
    isLoading: loginMutation.isPending || logoutMutation.isPending || refreshMutation.isPending,

    // Errors
    error: formattedError,
  };
}

export default useAuth;
